/**
 * The SignalR half of the contract, mirrored by hand from
 * `src/shared-contracts/Tunet.Contracts/Realtime`.
 *
 * Hand-written rather than generated, on purpose. OpenAPI only describes types reachable
 * from an HTTP route, and every mutation here travels over the hub instead; schemas
 * registered outside an operation come back with nested types inlined and nullability
 * erased, so `AvatarPoseUpdate.leftHand` would type as always-present when a hand losing
 * tracking is precisely the case this client has to handle.
 *
 * The C# interfaces remain the source of truth. `HubContractTests` in Tunet.Api.Tests
 * fails the build if the method set or any payload's property names drift from what is
 * declared here, so this file cannot rot silently.
 *
 * REST types are generated and imported — that part .NET describes accurately.
 */

import type {
  ConnectionDto,
  ConnectionStyleDto,
  ExportDto,
  NodeDto,
  NodeStyleDto,
  NodeTypeDto,
  RoomDto,
  RoomSnapshotDto,
  StrokeDto,
  TransformDto,
  Vec3,
} from '../generated/contracts';

export type {
  ConnectionDto,
  ConnectionStyleDto,
  ExportDto,
  NodeDto,
  NodeStyleDto,
  NodeTypeDto,
  RoomDto,
  RoomSnapshotDto,
  StrokeDto,
  TransformDto,
  Vec3,
};

export const HUB_ROUTE = '/hubs/room';

/**
 * Mirrors Tunet.Contracts.Dtos.NodeKind. Serialised as its numeric value.
 *
 * Shape is the only kind — notes were removed (plan/grid.md, "What participates — settled").
 * Kept as a discriminator rather than dropped outright because `NodeDto.kind` is already
 * threaded through persistence and the wire, and a future kind would want it back.
 */
export const NodeKind = {
  Shape: 0,
} as const;

export type NodeKindValue = (typeof NodeKind)[keyof typeof NodeKind];

/**
 * Narrows a kind read off the wire.
 *
 * The generated `NodeDto.kind` is a plain `number`, because .NET describes an integer enum
 * without listing its members. Rather than widen everything here to match, values are
 * narrowed at the boundary — and anything unrecognised becomes a shape, which is the safe
 * default since a shape always renders whereas an unknown kind would not.
 */
export function toNodeKind(_value: number): NodeKindValue {
  return NodeKind.Shape;
}

/**
 * Mirrors Tunet.Contracts.Dtos.ShapeTypes — the `primitives` category.
 *
 * Kept only as the fallback palette for a client that could not fetch the catalogue. The
 * real vocabulary comes from `GET /api/node-types`, which is generated rather than mirrored
 * precisely so a list this size cannot drift out of step with what the server accepts.
 */
export const ShapeTypes = ['box', 'sphere', 'cylinder', 'cone', 'capsule'] as const;

export type ShapeType = (typeof ShapeTypes)[number];

/** Mirrors Tunet.Contracts.Dtos.ArrowModes. A null `arrow` reads as `forward`. */
export const ArrowModes = {
  None: 'none',
  Forward: 'forward',
  Both: 'both',
} as const;

export type ArrowMode = (typeof ArrowModes)[keyof typeof ArrowModes];

/**
 * Mirrors Tunet.Contracts.Dtos.LineStyles. A null `line` reads as `solid`.
 *
 * No `None` member here, deliberately — see the C# side's own remarks on `LineStyles` for why:
 * it is a Link-mode UI action ("remove this connection"), not a persisted line style.
 */
export const LineStyles = {
  Solid: 'solid',
  Dashed: 'dashed',
  Dotted: 'dotted',
} as const;

export type LineStyle = (typeof LineStyles)[keyof typeof LineStyles];

/**
 * Narrows an arrow mode read off the wire, defaulting to forward.
 *
 * Forward rather than none because every edge here is drawn by dragging from one node to
 * another, so it always has a direction — and that makes edges stored before the field
 * existed read the way they were meant to.
 */
export function toArrowMode(value: string | null | undefined): ArrowMode {
  return value === ArrowModes.None || value === ArrowModes.Both ? value : ArrowModes.Forward;
}

export function toLineStyle(value: string | null | undefined): LineStyle {
  return value === LineStyles.Dashed || value === LineStyles.Dotted ? value : LineStyles.Solid;
}

/**
 * Mirrors Tunet.Contracts.Dtos.StrokeWidths. A null `width` reads as `medium` — every stroke
 * committed before this field existed has nothing stored here, and must keep rendering at
 * exactly the width it always did rather than being retroactively resized.
 */
export const StrokeWidths = {
  Fine: 'fine',
  Medium: 'medium',
  Fat: 'fat',
} as const;

export type StrokeWidth = (typeof StrokeWidths)[keyof typeof StrokeWidths];

export function toStrokeWidth(value: string | null | undefined): StrokeWidth {
  return value === StrokeWidths.Fine || value === StrokeWidths.Fat ? value : StrokeWidths.Medium;
}

/**
 * Mirrors Tunet.Contracts.Realtime.ToolModes. A peer's current authoring mode, broadcast on
 * change so remote avatars can show the right prop in their dominant hand
 * (plan/remote-tool-visibility.md) — Draw a marker, Scan a scanner, Link a linker, Build the
 * ordinary hand. Mode only, never the variant within it: Draw's marker widths and the eraser,
 * Link's twelve style combinations, none of it crosses the room.
 *
 * Lives here, not with `ShapeTypes`/`ArrowModes`/`LineStyles` above, for the reason the C# side's
 * own remarks give: those describe *persisted* data (a `ConnectionStyleDto` inside
 * `Connection.StyleJson`), and this never is — it only ever rides a live `ToolSwitched` broadcast
 * or the server's own in-memory `ToolSwitchRegistry`.
 */
export const ToolModes = {
  Build: 'build',
  Draw: 'draw',
  Scan: 'scan',
  Link: 'link',
} as const;

export type ToolModeValue = (typeof ToolModes)[keyof typeof ToolModes];

/**
 * Narrows a mode read off the wire, defaulting to Build — the same safe "holding nothing" state
 * a peer who has never switched away from Build is already in (no retained server-side entry at
 * all; see `ToolSwitchRegistry`'s own remarks), so an unrecognised value degrades to exactly the
 * state it would already be indistinguishable from, not a broken avatar.
 */
export function toToolMode(value: string | null | undefined): ToolModeValue {
  return value === ToolModes.Draw || value === ToolModes.Scan || value === ToolModes.Link
    ? value
    : ToolModes.Build;
}

/**
 * Mirrors Tunet.Contracts.Realtime.Hands — which physical hand a `ToolSwitched` event's prop
 * belongs on. Not derivable by the receiver: handedness is a per-user setting
 * (`input/Handedness.ts`) with nothing on `AvatarPoseUpdate` saying which of `leftHand`/
 * `rightHand` is dominant for a given peer, so the sender has to say so explicitly
 * (plan/remote-tool-visibility.md, "Handedness is the trap").
 */
export const Hands = {
  Left: 'left',
  Right: 'right',
} as const;

export type HandValue = (typeof Hands)[keyof typeof Hands];

/** Narrows a hand read off the wire, defaulting to right — matches `Handedness.ts`'s own stored
 *  default (`loadStored()`), so the two "right-handed by default" facts cannot disagree. */
export function toHand(value: string | null | undefined): HandValue {
  return value === Hands.Left ? value : Hands.Right;
}

// --- presence and locks -------------------------------------------------------------

export interface PresenceDto {
  peerId: string;
  displayName: string;
  /** Server-assigned. Tints this peer's avatar and any node they are holding. */
  colorHex: string;
  /**
   * Asserted by the client at `joinRoom` time (plan/roadmap.md, Phase 3) — a role, not a
   * security boundary; every mutation attempt from a spectator is refused server-side
   * regardless of what this flag says. Carried in presence so every other peer can render
   * one as a spectator — a head only, semi-transparent, with no hands.
   */
  isSpectator: boolean;
}

export interface NodeLockDto {
  nodeId: string;
  heldByPeerId: string;
}

export interface LockResult {
  nodeId: string;
  granted: boolean;
  /** Present only when refused. */
  heldByPeerId?: string | null;
}

export interface RoomJoinResult {
  peerId: string;
  self: PresenceDto;
  snapshot: RoomSnapshotDto;
  /** Everyone already in the room, excluding self. */
  peers: PresenceDto[];
  activeLocks: NodeLockDto[];
}

/**
 * What the room looks like once it has been pointed at a different diagram, or at none
 * (SPEC-161). Deliberately not a {@link RoomJoinResult}: nobody joins, nobody leaves, and the
 * peer list is exactly what it was — carrying `peerId`/`self`/`peers` back would invite a client
 * to re-run its join handling and tear down the presence this exists to preserve (SPEC-064).
 *
 * `snapshot.strokes` is unchanged by a swap: ink belongs to the room, not the diagram (SPEC-130).
 */
export interface DiagramShownResult {
  snapshot: RoomSnapshotDto;
  /** Grabs already held in the newly shown diagram, by anyone, in any room (SPEC-133). */
  activeLocks: NodeLockDto[];
}

// --- streaming ----------------------------------------------------------------------

/**
 * Packed rather than named vectors, because these fire 15-20 times a second per peer.
 * `p` is (x, y, z); `r` is a quaternion (x, y, z, w).
 */
export interface PoseDto {
  p: number[];
  r: number[];
}

export interface NodeTransformUpdate {
  nodeId: string;
  p: number[];
  r: number[];
  /** Uniform scale. */
  s: number;
  /** Sender's clock in epoch milliseconds; drives receive-side interpolation. */
  t: number;
  /** Stamped by the server on rebroadcast. Anything set here outbound is discarded. */
  peerId?: string;
}

export interface AvatarPoseUpdate {
  head: PoseDto;
  /** Null while the hand is untracked — out of view, or occluded. */
  leftHand: PoseDto | null;
  rightHand: PoseDto | null;
  leftPinch: boolean;
  rightPinch: boolean;
  t: number;
  peerId?: string;
}

/**
 * New points captured for a stroke still in progress. High-frequency, fire-and-forget,
 * never persisted — the durable write happens once, in `CommitStroke`, from the full
 * point list the client committed, not from anything sent here.
 *
 * Packed rather than `Vec3[]` for the same reason `NodeTransformUpdate` is: named-object
 * JSON roughly triples the byte count. The capture-and-flush cadence this assumes is not
 * implemented by any client yet — see the remarks on the mirrored C# type.
 */
export interface StrokePointUpdate {
  strokeId: string;
  /** New points since the last flush, each (x, y, z), in Room-local space. */
  points: number[][];
  /** Sender's clock in epoch milliseconds; drives receive-side interpolation. */
  t: number;
  /** Stamped by the server on rebroadcast. Anything set here outbound is discarded. */
  peerId?: string;
}

// --- requests -----------------------------------------------------------------------

export interface CreateNodeRequest {
  /** Client-generated, so the ghost in the user's hand is already the real node. */
  nodeId: string;
  kind: NodeKindValue;
  shapeType?: string | null;
  text?: string | null;
  style: NodeStyleDto;
  transform: TransformDto;
}

export interface UpdateNodeRequest {
  nodeId: string;
  /** Null or absent leaves the existing value alone. */
  text?: string | null;
  style?: NodeStyleDto | null;
}

export interface CreateConnectionRequest {
  connectionId: string;
  fromNodeId: string;
  toNodeId: string;
  label?: string | null;
  style: ConnectionStyleDto;
}

export interface UpdateConnectionRequest {
  connectionId: string;
  /** Null or absent leaves the existing label alone. Empty string clears it. */
  label?: string | null;
  /** Null or absent leaves the existing style alone. Set fields are applied. */
  style?: ConnectionStyleDto | null;
  /** Swaps the endpoints, for an arrow drawn the wrong way round. */
  reverse?: boolean;
}

/**
 * Commits a finished stroke: the full point list the client captured, not a diff. The
 * server simplifies this once, server-side, before it becomes durable, and hands back
 * the simplified `StrokeDto` — the caller has to apply that result the same way
 * `CreateNode`'s caller does, or the drawer keeps seeing their crisper local copy forever.
 */
export interface CommitStrokeRequest {
  /** Client-generated, the same convention CreateNodeRequest.nodeId uses. */
  strokeId: string;
  color: string;
  /** See StrokeWidths. Null or absent means StrokeWidths.Default (medium). */
  width?: string | null;
  /** Every point captured, in drawing order, un-simplified. */
  points: Vec3[];
}

// --- assistant -----------------------------------------------------------------------

/**
 * Outcome of `beginAssistantTurn`. A refusal is a normal, expected return value — someone
 * else's turn is already open, or no assistant is configured on this server — not an
 * exception; see `roomHub.ts`.
 */
export interface AssistantTurnResult {
  granted: boolean;
  /** Machine-readable when refused: `"not-configured"` or `"already-open"` today. Absent when granted. */
  reason?: string | null;
}

/**
 * One chunk of a captured utterance, streamed while a turn is open. Mirrors
 * `Tunet.Contracts.Realtime.AssistantAudioChunkUpdate`.
 */
export interface AssistantAudioChunkUpdate {
  /**
   * Base64-encoded bytes, C#'s own wire convention for `byte[]` — not a packed number
   * array the way pose and stroke updates use, since there is no fixed element width to
   * exploit the way there is for floats. See `net/packing.ts`'s `bytesToBase64` /
   * `base64ToBytes`.
   */
  data: string;
}

/**
 * One chunk of a peer's own live microphone capture, relayed to the rest of the room —
 * player-to-player voice, not the assistant (plan/ai-in-room.md). Mirrors
 * `Tunet.Contracts.Realtime.VoiceChunkUpdate`.
 */
export interface VoiceChunkUpdate {
  /** Base64-encoded bytes — see `AssistantAudioChunkUpdate.data`'s own remarks. */
  data: string;
  /** Stamped by the server on rebroadcast. Anything set here outbound is discarded. */
  peerId?: string;
}

// --- speaking a node's label ---------------------------------------------------------

/**
 * Outcome of `beginLabelCapture`. A refusal is a normal, expected return value — no
 * assistant configured on this server (labelling reuses the same Gemini endpoint), or this
 * connection already has a capture open for a different node — not an exception. Mirrors
 * `Tunet.Contracts.Realtime.LabelCaptureResult`.
 */
export interface LabelCaptureResult {
  granted: boolean;
  /** Machine-readable when refused: `"not-configured"` or `"already-open"`. Absent when granted. */
  reason?: string | null;
}

/**
 * One chunk of a captured "speak the label" clip, streamed while a label capture is open.
 * Mirrors `Tunet.Contracts.Realtime.LabelAudioChunkUpdate`.
 */
export interface LabelAudioChunkUpdate {
  /** Base64-encoded bytes — see `AssistantAudioChunkUpdate.data`'s own remarks. */
  data: string;
}

/**
 * One spoken clip of one assistant turn's own ordered sequence of utterances. Broadcast to
 * the whole room, the asker included — there is nothing to apply optimistically, and the
 * asker needs to hear it too.
 *
 * A turn with no tool call sends exactly one of these (`isFinal: true`). A turn with one
 * sends two, in order — the acknowledgement first (`isFinal: false`), then, only once every
 * tool call has at least been attempted server-side, the confirmation (`isFinal: true`) —
 * never the reverse: plan/ai-in-room.md, "Speak, then act, then speak." A client should
 * queue every arrival and play them strictly in order, one at a time, never overlapping —
 * see `AssistantController.handleReply` — rather than assuming a fixed count of two: nothing
 * about this shape stops a future turn from carrying more.
 */
export interface AssistantReplyDto {
  /** What the model heard, transcribed from the captured audio. The same on every
   *  utterance of one turn. */
  transcript: string;
  /** What this one clip says, as text — the same string that was synthesised into `audio`. */
  text: string;
  /** Base64-encoded synthesised speech for this one clip. Never persisted anywhere, client
   *  included. */
  audio: string;
  /** MIME type of `audio`, e.g. `"audio/mpeg"` or `"audio/wav"`. */
  audioMimeType: string;
  byPeerId: string;
  /** This clip's position in its turn's own ordered sequence, from zero. */
  utteranceIndex: number;
  /** Whether this is the last utterance of its turn — see this interface's own doc for why
   *  a client must wait for this rather than inferring "done" from an empty queue alone. */
  isFinal: boolean;
}

/**
 * Announces one utterance's speech is about to arrive as a sequence of raw PCM chunks
 * (`AssistantSpeechChunkDto`), followed by a matching `assistantSpeechEnded` — the streaming
 * counterpart to `AssistantReplyDto`, sent instead of it for an utterance whose synthesis used
 * the server's streaming text-to-speech path rather than falling back to the unary one.
 * Mirrors `Tunet.Contracts.Realtime.AssistantSpeechStartedDto`.
 */
export interface AssistantSpeechStartedDto {
  transcript: string;
  text: string;
  /** Not a real MIME type — LINEAR16 is headerless raw PCM with none — carried only as a
   *  human-readable label; playback decodes chunks against `sampleRateHertz`, not this
   *  string. See `AssistantSpeechPlayer.ts`'s own module doc. */
  audioMimeType: string;
  /** The fixed sample rate every chunk of this utterance is encoded at. */
  sampleRateHertz: number;
  byPeerId: string;
  utteranceIndex: number;
  isFinal: boolean;
}

/**
 * One chunk of raw LINEAR16 PCM for an utterance already announced by
 * `AssistantSpeechStartedDto`. Mirrors `Tunet.Contracts.Realtime.AssistantSpeechChunkDto`.
 */
export interface AssistantSpeechChunkDto {
  utteranceIndex: number;
  /** Base64-encoded bytes — see `AssistantAudioChunkUpdate.data`'s own remarks. */
  data: string;
}

// --- hub surface --------------------------------------------------------------------

/**
 * Client-to-server method names. Mirrors IRoomHubServer.
 *
 * Names alone are not the whole contract — see `ServerMethodParameters` below.
 */
export const ServerMethod = {
  JoinRoom: 'JoinRoom',
  LeaveRoom: 'LeaveRoom',
  ShowDiagram: 'ShowDiagram',
  CreateNode: 'CreateNode',
  UpdateNode: 'UpdateNode',
  DeleteNode: 'DeleteNode',
  CreateConnection: 'CreateConnection',
  UpdateConnection: 'UpdateConnection',
  DeleteConnection: 'DeleteConnection',
  CommitStroke: 'CommitStroke',
  DeleteStroke: 'DeleteStroke',
  AcquireNodeLock: 'AcquireNodeLock',
  ReleaseNodeLock: 'ReleaseNodeLock',
  StreamNodeTransform: 'StreamNodeTransform',
  StreamAvatarPose: 'StreamAvatarPose',
  StreamStrokePoint: 'StreamStrokePoint',
  StartVoiceStream: 'StartVoiceStream',
  StreamVoiceChunk: 'StreamVoiceChunk',
  EndVoiceStream: 'EndVoiceStream',
  BeginAssistantTurn: 'BeginAssistantTurn',
  StreamAssistantAudioChunk: 'StreamAssistantAudioChunk',
  EndAssistantTurn: 'EndAssistantTurn',
  ClearAssistantHistory: 'ClearAssistantHistory',
  UpdateRoomAccent: 'UpdateRoomAccent',
  ExportWhiteboard: 'ExportWhiteboard',
  ExportMermaid: 'ExportMermaid',
  BeginLabelCapture: 'BeginLabelCapture',
  StreamLabelAudioChunk: 'StreamLabelAudioChunk',
  EndLabelCapture: 'EndLabelCapture',
  SwitchTool: 'SwitchTool',
} as const;

/**
 * Each server method's arguments, in order, mirroring `IRoomHubServer`'s signatures.
 *
 * Documentation with a test behind it: `HubContractTests` compares this list against the
 * C# parameter names and fails the build on drift. It exists because the method-name
 * mirror was not enough — `JoinRoom` lost its `displayName` argument when the server
 * started stamping identity, and a rename check cannot see an argument disappear. What
 * breaks a caller is arity, so arity is what is guarded.
 */
export const ServerMethodParameters = {
  JoinRoom: ['roomId', 'asSpectator'],
  LeaveRoom: ['roomId'],
  ShowDiagram: ['roomId', 'diagramId'],
  CreateNode: ['roomId', 'request'],
  UpdateNode: ['roomId', 'request'],
  DeleteNode: ['roomId', 'nodeId'],
  CreateConnection: ['roomId', 'request'],
  UpdateConnection: ['roomId', 'request'],
  DeleteConnection: ['roomId', 'connectionId'],
  CommitStroke: ['roomId', 'request'],
  DeleteStroke: ['roomId', 'strokeId'],
  AcquireNodeLock: ['roomId', 'nodeId'],
  ReleaseNodeLock: ['roomId', 'nodeId', 'finalTransform'],
  StreamNodeTransform: ['update'],
  StreamAvatarPose: ['update'],
  StreamStrokePoint: ['update'],
  StartVoiceStream: ['mimeType'],
  StreamVoiceChunk: ['update'],
  EndVoiceStream: [],
  BeginAssistantTurn: ['roomId', 'mimeType'],
  StreamAssistantAudioChunk: ['update'],
  EndAssistantTurn: ['roomId'],
  ClearAssistantHistory: ['roomId'],
  UpdateRoomAccent: ['roomId', 'accentColor'],
  ExportWhiteboard: ['roomId'],
  ExportMermaid: ['roomId'],
  BeginLabelCapture: ['roomId', 'nodeId', 'mimeType'],
  StreamLabelAudioChunk: ['update'],
  EndLabelCapture: ['roomId'],
  SwitchTool: ['mode', 'hand'],
} as const satisfies Record<keyof typeof ServerMethod, readonly string[]>;

/** Server-to-client event names. Mirrors IRoomHubClient. */
export const ClientEvent = {
  PeerJoined: 'PeerJoined',
  DiagramShown: 'DiagramShown',
  PeerLeft: 'PeerLeft',
  NodeCreated: 'NodeCreated',
  NodeUpdated: 'NodeUpdated',
  NodeDeleted: 'NodeDeleted',
  ConnectionCreated: 'ConnectionCreated',
  ConnectionUpdated: 'ConnectionUpdated',
  ConnectionDeleted: 'ConnectionDeleted',
  StrokeCreated: 'StrokeCreated',
  StrokeDeleted: 'StrokeDeleted',
  NodeLockAcquired: 'NodeLockAcquired',
  NodeLockReleased: 'NodeLockReleased',
  NodeTransform: 'NodeTransform',
  AvatarPose: 'AvatarPose',
  StrokePoint: 'StrokePoint',
  VoiceStreamStarted: 'VoiceStreamStarted',
  VoiceChunk: 'VoiceChunk',
  VoiceStreamEnded: 'VoiceStreamEnded',
  AssistantTurnStarted: 'AssistantTurnStarted',
  AssistantReply: 'AssistantReply',
  AssistantSpeechStarted: 'AssistantSpeechStarted',
  AssistantSpeechChunk: 'AssistantSpeechChunk',
  AssistantSpeechEnded: 'AssistantSpeechEnded',
  AssistantTurnFailed: 'AssistantTurnFailed',
  AssistantHistoryCleared: 'AssistantHistoryCleared',
  RoomUpdated: 'RoomUpdated',
  LabelCaptureFailed: 'LabelCaptureFailed',
  ToolSwitched: 'ToolSwitched',
} as const;

/** Every server-to-client event, with its payload. */
export interface RoomHubEvents {
  peerJoined: (peer: PresenceDto) => void;
  diagramShown: (shown: DiagramShownResult, byPeerId: string) => void;
  peerLeft: (peerId: string) => void;
  nodeCreated: (node: NodeDto, byPeerId: string) => void;
  nodeUpdated: (node: NodeDto, byPeerId: string) => void;
  nodeDeleted: (nodeId: string, byPeerId: string) => void;
  connectionCreated: (connection: ConnectionDto, byPeerId: string) => void;
  connectionUpdated: (connection: ConnectionDto, byPeerId: string) => void;
  connectionDeleted: (connectionId: string, byPeerId: string) => void;
  strokeCreated: (stroke: StrokeDto, byPeerId: string) => void;
  strokeDeleted: (strokeId: string, byPeerId: string) => void;
  nodeLockAcquired: (nodeId: string, byPeerId: string) => void;
  nodeLockReleased: (nodeId: string, finalTransform: TransformDto, newVersion: number) => void;
  nodeTransform: (update: NodeTransformUpdate) => void;
  avatarPose: (update: AvatarPoseUpdate) => void;
  strokePoint: (update: StrokePointUpdate) => void;
  voiceStreamStarted: (peerId: string, mimeType: string) => void;
  voiceChunk: (update: VoiceChunkUpdate) => void;
  voiceStreamEnded: (peerId: string) => void;
  assistantTurnStarted: (byPeerId: string) => void;
  assistantReply: (reply: AssistantReplyDto) => void;
  assistantSpeechStarted: (started: AssistantSpeechStartedDto) => void;
  assistantSpeechChunk: (chunk: AssistantSpeechChunkDto) => void;
  assistantSpeechEnded: (utteranceIndex: number) => void;
  assistantTurnFailed: (reason: string) => void;
  assistantHistoryCleared: () => void;
  roomUpdated: (room: RoomDto, byPeerId: string) => void;
  labelCaptureFailed: (nodeId: string, reason: string) => void;
  /** See `ServerMethod.SwitchTool` and `IRoomHubClient.ToolSwitched`'s own remarks — including,
   *  once per join, a replay of every already-present peer's retained mode, addressed to the
   *  joining connection alone rather than broadcast to the room. */
  toolSwitched: (peerId: string, mode: string, hand: string) => void;
}
